import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Camera, Loader2, Sparkles, Check, ArrowLeft, UserPlus, AlertTriangle, MapPinned, UserCog } from "lucide-react";
import DuplicatePhoneModal from "@/components/DuplicatePhoneModal";
import { fixPastYear, todayIso, findPhoneMatch } from "@/lib/clientHelpers";

const DIAS_SHORT = ["SEG", "TER", "QUA", "QUI", "SEX", "SAB", "DOM"];

export default function ScanCarne() {
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const fileRef = useRef();
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [loading, setLoading] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [rotas, setRotas] = useState([]);
  const [cobradores, setCobradores] = useState([]);
  const [rotaId, setRotaId] = useState("");
  const [cobradorId, setCobradorId] = useState("");
  const [saving, setSaving] = useState(false);
  const [dateAdjusted, setDateAdjusted] = useState(false);
  const [duplicate, setDuplicate] = useState(null); // existing cliente found by phone
  const [pendingMode, setPendingMode] = useState(null); // "sale" | "only"

  useEffect(() => {
    api.get("/clientes").then((r) => setClientes(r.data)).catch(() => {});
    api.get("/rotas").then((r) => setRotas(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/staff").then((r) => setCobradores(r.data.filter((s) => s.roles?.cobrador && s.active))).catch(() => {});
    }
  }, [isAdmin]);

  const pickFile = () => fileRef.current?.click();

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp|jpg)$/.test(f.type)) {
      toast.error("Use foto JPG, PNG ou WEBP");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result;
      setPreview(url);
      setB64(url.split(",")[1]);
    };
    reader.readAsDataURL(f);
  };

  const analisar = async () => {
    if (!b64) return;
    setLoading(true);
    setExtracted(null);
    setDateAdjusted(false);
    try {
      const r = await api.post("/ai/scan-carne", { image_base64: b64 });
      const d = r.data.extracted || {};
      const rawDate = d.primeira_data || todayIso();
      const { date: fixedDate, adjusted } = fixPastYear(rawDate);
      setDateAdjusted(adjusted);
      setExtracted({
        cliente_nome: d.cliente_nome || "",
        telefone: d.telefone || "",
        endereco: d.endereco || "",
        bairro: d.bairro || "",
        descricao: d.descricao || "",
        valor_total: d.valor_total ?? "",
        entrada: d.entrada ?? 0,
        num_parcelas: d.num_parcelas ?? 4,
        valor_parcela: d.valor_parcela ?? "",
        frequencia: d.frequencia || "mensal",
        primeira_data: fixedDate || todayIso(),
        observacoes: d.observacoes || "",
        _erro: d.erro,
        _raw: d.raw,
      });
      if (adjusted) {
        toast.message("Ano da data ajustado para o futuro — confira", { duration: 5000 });
      } else {
        toast.success("Dados extraídos! Confira e ajuste se precisar.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro na análise");
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setExtracted((e) => ({ ...e, [k]: v }));

  const findExisting = () => {
    const n = (extracted?.cliente_nome || "").trim().toLowerCase();
    if (!n) return null;
    return clientes.find((c) => c.nome.toLowerCase() === n)
      || clientes.find((c) => c.nome.toLowerCase().includes(n) || n.includes(c.nome.toLowerCase()))
      || null;
  };

  const createClienteNow = async () => {
    const payload = {
      nome: extracted.cliente_nome.trim(),
      telefone: extracted.telefone || "",
      bairro: (extracted.bairro || "").trim(),
      endereco: (extracted.endereco || "").trim(),
      observacoes: (extracted.observacoes || "").trim(),
      rota_id: rotaId || null,
    };
    if (isAdmin) payload.assigned_cobrador_id = cobradorId || null;
    const r = await api.post("/clientes", payload);
    return r.data;
  };

  // Se o cliente já existe e usuário mexeu em rota/cobrador OU a IA leu endereço/bairro
  // novo, complementa o cadastro antes de ir pra venda. Nunca sobrescreve dados existentes.
  const applyRotaCobradorToExisting = async (existing) => {
    const rotaChanged = rotaId !== "" && (rotaId || null) !== (existing.rota_id || null);
    const cobradorChanged = isAdmin && cobradorId !== "" && (cobradorId || null) !== (existing.assigned_cobrador_id || null);
    const newEndereco = (extracted?.endereco || "").trim();
    const newBairro = (extracted?.bairro || "").trim();
    const enderecoFill = !existing.endereco && newEndereco;
    const bairroFill = !existing.bairro && newBairro;
    if (!rotaChanged && !cobradorChanged && !enderecoFill && !bairroFill) return;
    const payload = {
      nome: existing.nome,
      telefone: existing.telefone || "",
      bairro: enderecoFill || bairroFill ? (existing.bairro || newBairro) : (existing.bairro || ""),
      endereco: enderecoFill ? newEndereco : (existing.endereco || ""),
      observacoes: existing.observacoes || "",
      rota_id: rotaChanged ? (rotaId || null) : (existing.rota_id || null),
    };
    if (bairroFill) payload.bairro = newBairro;
    if (isAdmin) payload.assigned_cobrador_id = cobradorChanged ? (cobradorId || null) : (existing.assigned_cobrador_id || null);
    try { await api.put(`/clientes/${existing.id}`, payload); } catch (_e) { /* ignore */ }
  };

  const goToVendaWith = (clienteId) => {
    sessionStorage.setItem(
      "cf_ai_draft",
      JSON.stringify({
        cliente_id: clienteId,
        descricao: extracted.descricao,
        valor_total: extracted.valor_total,
        entrada: extracted.entrada,
        num_parcelas: extracted.num_parcelas,
        valor_parcela: extracted.valor_parcela,
        frequencia: extracted.frequencia,
        primeira_data: extracted.primeira_data,
        observacoes: extracted.observacoes,
      })
    );
    nav("/nova-venda");
  };

  const cadastrarEIrParaVenda = async () => {
    if (!extracted?.cliente_nome) return toast.error("Informe o nome do cliente");
    setSaving(true);
    try {
      const existingByName = findExisting();
      if (existingByName && !extracted.telefone) {
        await applyRotaCobradorToExisting(existingByName);
        toast.success(`Cliente já cadastrado: ${existingByName.nome}`);
        return goToVendaWith(existingByName.id);
      }
      const match = await findPhoneMatch(extracted.telefone);
      if (match) {
        setDuplicate(match);
        setPendingMode("sale");
        setSaving(false);
        return;
      }
      const cli = await createClienteNow();
      toast.success(`Cliente cadastrado: ${cli.nome}`);
      goToVendaWith(cli.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar cliente");
    } finally {
      setSaving(false);
    }
  };

  const soCadastrarCliente = async () => {
    if (!extracted?.cliente_nome) return toast.error("Informe o nome do cliente");
    setSaving(true);
    try {
      const match = await findPhoneMatch(extracted.telefone);
      if (match) {
        setDuplicate(match);
        setPendingMode("only");
        setSaving(false);
        return;
      }
      const cli = await createClienteNow();
      toast.success(`Cliente cadastrado: ${cli.nome}`);
      nav(`/clientes/${cli.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar");
    } finally {
      setSaving(false);
    }
  };

  const handleUseExisting = async (existing) => {
    setDuplicate(null);
    await applyRotaCobradorToExisting(existing);
    if (pendingMode === "sale") {
      toast.success(`Usando cliente existente: ${existing.nome}`);
      goToVendaWith(existing.id);
    } else {
      nav(`/clientes/${existing.id}`);
    }
  };

  const handleCreateAnyway = async () => {
    setDuplicate(null);
    setSaving(true);
    try {
      const cli = await createClienteNow();
      toast.success(`Cliente cadastrado: ${cli.nome}`);
      if (pendingMode === "sale") goToVendaWith(cli.id);
      else nav(`/clientes/${cli.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao cadastrar");
    } finally {
      setSaving(false);
    }
  };

  const existing = extracted ? findExisting() : null;

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-sm text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900 mb-1">Escanear Carnê</h1>
      <p className="text-sm text-stone-500 mb-5">Tire uma foto do carnê ou cartão manuscrito. A IA lê os dados e cadastra o cliente.</p>

      <input
        data-testid="carne-camera-upload-input"
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        capture="environment"
        onChange={onFile}
        className="hidden"
      />

      <div className="carne-card p-5">
        {!preview ? (
          <button
            data-testid="btn-open-camera"
            onClick={pickFile}
            className="w-full aspect-[4/3] rounded-xl border-2 border-dashed border-[color:var(--terracotta)]/40 bg-[color:var(--terracotta-light)]/40 flex flex-col items-center justify-center gap-2 text-[color:var(--terracotta)]"
          >
            <Camera className="w-10 h-10" strokeWidth={1.8} />
            <div className="font-display font-bold">Tirar foto ou escolher</div>
            <div className="text-xs text-stone-500">JPG, PNG ou WEBP</div>
          </button>
        ) : (
          <div>
            <img
              data-testid="carne-preview-image"
              src={preview}
              alt="Carnê"
              className="w-full rounded-xl border border-stone-200 max-h-[420px] object-contain bg-stone-50"
            />
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                onClick={() => { setPreview(null); setB64(null); setExtracted(null); setDateAdjusted(false); }}
                className="h-12 rounded-xl border border-stone-200 font-bold text-stone-700"
              >
                Trocar foto
              </button>
              <button
                data-testid="btn-process-ai-ocr"
                onClick={analisar}
                disabled={loading}
                className="btn-primary"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {loading ? "Analisando..." : "Analisar com IA"}
              </button>
            </div>
          </div>
        )}
      </div>

      {extracted && !extracted._erro && (
        <div className="carne-card p-5 mt-4 animate-slide-up" data-testid="ai-extracted-results-container">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-5 h-5 text-[color:var(--terracotta)]" />
            <h2 className="font-display text-lg font-bold">Confira e ajuste os dados</h2>
          </div>

          {existing && (
            <div className="mb-3 text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-2">
              ✓ Cliente <b>{existing.nome}</b> já está cadastrado. Vamos usar ele.
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Nome do Cliente*" testid="ex-nome">
              <input value={extracted.cliente_nome} onChange={(e) => set("cliente_nome", e.target.value)} data-testid="input-ex-nome" className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white" />
            </F>
            <F label="Telefone" testid="ex-tel">
              <input value={extracted.telefone} onChange={(e) => set("telefone", e.target.value)} data-testid="input-ex-tel" placeholder="5511..." className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white" />
            </F>
            <F label="Endereço / Rua" testid="ex-endereco" full>
              <input value={extracted.endereco} onChange={(e) => set("endereco", e.target.value)} data-testid="input-ex-endereco" placeholder="Ex: Rua das Flores, 123" className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white" />
            </F>
            <F label="Bairro" testid="ex-bairro" full>
              <input value={extracted.bairro} onChange={(e) => set("bairro", e.target.value)} data-testid="input-ex-bairro" placeholder="Ex: Centro" className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white" />
            </F>
            <F label="Item / Descrição" testid="ex-desc" full>
              <input value={extracted.descricao} onChange={(e) => set("descricao", e.target.value)} data-testid="input-ex-desc" className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white" />
            </F>
            <F label="Valor Total (R$)" testid="ex-valor">
              <input type="number" step="0.01" value={extracted.valor_total} onChange={(e) => set("valor_total", e.target.value)} data-testid="input-ex-valor" className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white" />
            </F>
            <F label="Valor da Parcela (R$)" testid="ex-vparcela">
              <input type="number" step="0.01" value={extracted.valor_parcela} onChange={(e) => set("valor_parcela", e.target.value)} data-testid="input-ex-vparcela" placeholder="Opcional" className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white" />
            </F>
            <F label="Entrada (R$)" testid="ex-entrada">
              <input type="number" step="0.01" value={extracted.entrada} onChange={(e) => set("entrada", e.target.value)} data-testid="input-ex-entrada" className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white" />
            </F>
            <F label="Nº Parcelas" testid="ex-parc">
              <input type="number" min="1" value={extracted.num_parcelas} onChange={(e) => set("num_parcelas", e.target.value)} data-testid="input-ex-parc" className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white" />
            </F>
            <F label="Frequência" testid="ex-freq">
              <select value={extracted.frequencia} onChange={(e) => set("frequencia", e.target.value)} data-testid="input-ex-freq" className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white">
                <option value="semanal">Semanal</option>
                <option value="quinzenal">Quinzenal</option>
                <option value="mensal">Mensal</option>
              </select>
            </F>
            <F label="1ª data de vencimento" testid="ex-data">
              <input
                type="date"
                value={extracted.primeira_data}
                min={todayIso()}
                onChange={(e) => { set("primeira_data", e.target.value); setDateAdjusted(false); }}
                data-testid="input-ex-data"
                className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white"
              />
              {dateAdjusted && (
                <div className="mt-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1" data-testid="warn-year-adjusted">
                  <AlertTriangle className="w-3 h-3" /> Ano ajustado para ficar no futuro — confira
                </div>
              )}
            </F>
            <F label={<span className="inline-flex items-center gap-1"><MapPinned className="w-3 h-3" /> Rota de cobrança</span>} testid="ex-rota">
              <select
                data-testid="input-ex-rota"
                value={rotaId}
                onChange={(e) => setRotaId(e.target.value)}
                className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white"
              >
                <option value="">— Sem rota —</option>
                {rotas.map((r) => (
                  <option key={r.id} value={r.id}>{r.nome} ({DIAS_SHORT[r.dia_semana]})</option>
                ))}
              </select>
            </F>
            {isAdmin && (
              <F label={<span className="inline-flex items-center gap-1"><UserCog className="w-3 h-3" /> Cobrador responsável</span>} testid="ex-cobrador">
                <select
                  data-testid="input-ex-cobrador"
                  value={cobradorId}
                  onChange={(e) => setCobradorId(e.target.value)}
                  className="w-full h-11 px-3 rounded-lg border border-stone-200 bg-white"
                >
                  <option value="">— Nenhum —</option>
                  {cobradores.map((c) => (
                    <option key={c.id} value={c.id}>{c.name} (@{c.username})</option>
                  ))}
                </select>
              </F>
            )}
            <F label="Observações" testid="ex-obs" full>
              <textarea
                value={extracted.observacoes}
                onChange={(e) => set("observacoes", e.target.value)}
                data-testid="input-ex-obs"
                placeholder="Somente anotações reais (ex: entregar amanhã)"
                className="w-full min-h-[70px] p-3 rounded-lg border border-stone-200 bg-white"
              />
            </F>
          </div>

          {extracted.valor_total && (
            <div className="mt-3 text-xs text-stone-500">
              Cada parcela ~ <b>{money(Math.max(0, (parseFloat(extracted.valor_total) || 0) - (parseFloat(extracted.entrada) || 0)) / Math.max(1, parseInt(extracted.num_parcelas) || 1))}</b>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
            <button
              data-testid="btn-cadastrar-cliente-only"
              onClick={soCadastrarCliente}
              disabled={saving}
              className="h-12 rounded-xl border border-stone-300 font-bold text-stone-700 inline-flex items-center justify-center gap-2 hover:bg-stone-50"
            >
              <UserPlus className="w-4 h-4" /> Só cadastrar cliente
            </button>
            <button
              data-testid="btn-confirm-extracted-sale"
              onClick={cadastrarEIrParaVenda}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {existing ? "Ir para nova venda" : "Cadastrar + criar venda"}
            </button>
          </div>
        </div>
      )}

      {extracted?._erro && (
        <div className="carne-card p-5 mt-4">
          <div className="text-sm text-rose-700 bg-rose-50 rounded-lg p-3">
            {extracted._erro}
            <div className="text-xs text-stone-600 mt-2">{extracted._raw}</div>
          </div>
        </div>
      )}

      <DuplicatePhoneModal
        open={!!duplicate}
        existing={duplicate}
        onUseExisting={handleUseExisting}
        onCreateAnyway={handleCreateAnyway}
        onCancel={() => setDuplicate(null)}
        mode="scan"
      />
    </div>
  );
}

function F({ label, children, full, testid }) {
  return (
    <div className={full ? "sm:col-span-2" : ""} data-testid={testid}>
      <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1">{label}</div>
      {children}
    </div>
  );
}
